import { Box, Dialog, TextField, Typography } from '@mui/material'
import { memo, useCallback, useMemo, useState } from 'react'
import theme from '../theme'

/**
 * A long multiple choice — a hundred and fifty leagues, a few thousand teams — asked for in a window of
 * its own rather than in the filter row. A field that grows a chip per choice pushes everything below it
 * down the page and eventually wraps into a second and third line; this keeps the row exactly one line
 * tall and gives the list somewhere to be read.
 *
 * The trigger says what is chosen and how much of it. The window keeps a draft while it is open and
 * hands it to the board when it closes, so ticking six leagues costs one rebuild rather than six.
 */

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

/**
 * Two hundred leagues is two hundred rows, and a ticked box re-renders the lot. A row of MUI's own
 * Checkbox — a ButtonBase with a ripple apiece — cost a second and a half per tick at that length, so
 * this is a plain input with the palette's colour, memoised on its own state.
 */
const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '7px 16px',
  fontFamily: FONT,
  fontSize: '14px',
  '&:hover': { backgroundColor: '#F6F6F8' }
}

const BOX_SX = { width: '15px', height: '15px', accentColor: theme.palette.primary.light, cursor: 'pointer' }

// the longest list here is every team in the feed; past this many the search box is the way through
const MAX_SHOWN = 200

/**
 * A star beside the box, where the list is one somebody stars from — the teams. Ticking a team narrows
 * the board to it; starring it marks every game it is playing, which is the thing anybody looking up a
 * team by name usually wants next.
 */
const STAR_SX = {
  padding: '2px 4px',
  borderRadius: '2px',
  // the same size as the name beside it: a star half the height of the row reads as decoration
  fontSize: '14px',
  lineHeight: 1,
  cursor: 'pointer',
  transition: 'color 0.15s, background-color 0.15s',
  '&:hover': { color: '#E8B10A', backgroundColor: '#EDEDF0' }
}

const Row = memo(function Row({ option, checked, onToggle, starred, onStar }) {
  /**
   * Where there is a star, the row stops being one big target: the box and the star each answer for
   * themselves and the name answers for nothing. A whole row that ticks the box means every miss of a
   * thirteen pixel star filtered the board to a team nobody asked for.
   */
  const exact = Boolean(onStar)
  return (
    <Box
      component={exact ? 'div' : 'label'}
      sx={{ ...ROW_SX, cursor: exact ? 'default' : 'pointer' }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(option)}
        sx={BOX_SX}
      />
      {onStar && (
        <Box
          component="span"
          title={starred ? 'Remove this team\u2019s games from favourites' : 'Add this team\u2019s games to favourites'}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onStar(option) }}
          sx={{ ...STAR_SX, color: starred ? '#E8B10A' : '#C4C7CA' }}
        >
          {starred ? '★' : '☆'}
        </Box>
      )}
      {option}
    </Box>
  )
})

function PickerDialog({ label, plural, options, value, onChange, width, star }) {
  const [ open, setOpen ] = useState(false)
  const [ query, setQuery ] = useState('')

  /**
   * The window holds its own draft and hands it over when it closes. Applying every tick straight to the
   * board meant rebuilding a thousand rows between one checkbox and the next — a tenth of a second each,
   * and worse the less the board is filtered — which is felt as a picker that lags behind the mouse.
   */
  const [ draft, setDraft ] = useState(value)

  const start = () => {
    setDraft(value)
    setQuery('')
    setOpen(true)
  }

  const finish = () => {
    setOpen(false)
    if (JSON.stringify(draft) !== JSON.stringify(value)) {
      onChange(draft)
    }
  }

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matching = needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options
    // what was already ticked when the window opened stays at the top, so a tick does not move the list
    // out from under the next click
    const chosen = matching.filter((o) => value.includes(o))
    const rest = matching.filter((o) => !value.includes(o))
    return [...chosen, ...rest]
    // eslint-disable-next-line
  }, [options, query, open])

  const toggle = useCallback((option) => {
    setDraft((prev) => (prev.includes(option) ? prev.filter((v) => v !== option) : [...prev, option]))
  }, [])

  /**
   * The star moves on the click rather than on the answer, and the feed is several seconds behind a
   * change — long enough that a star waiting for it reads as a click that did nothing.
   */
  const [ pending, setPending ] = useState({})
  const onStar = useCallback((option) => {
    if (!star) {
      return
    }
    const wanted = !(option in pending ? pending[option] : star.isOn(option))
    setPending((prev) => ({ ...prev, [option]: wanted }))
    Promise.resolve(star.toggle(option, wanted)).catch(() => {
      setPending((prev) => ({ ...prev, [option]: !wanted }))
    })
    // eslint-disable-next-line
  }, [star, pending])

  const isStarred = (option) => (option in pending ? pending[option] : Boolean(star && star.isOn(option)))

  const summary = value.length === 0
    ? `Any ${label.toLowerCase()}`
    : (value.length === 1 ? value[0] : `${value.length} ${plural}`)

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={start}
        title={value.length > 1 ? value.join(', ') : undefined}
        sx={{
          width,
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '0 12px',
          border: `1px solid ${value.length ? theme.palette.primary.light : '#C9C9D1'}`,
          borderRadius: '2px',
          backgroundColor: 'white',
          fontFamily: FONT,
          fontSize: '14px',
          color: value.length ? '#3C4043' : '#9A9EA3',
          cursor: 'pointer',
          textAlign: 'left',
          '&:hover': { borderColor: theme.palette.primary.light }
        }}
      >
        {/* no caret: this is a button that opens a window, and dressing it as a dropdown promised a
            list that would unfold in place */}
        <Box component="span" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary}
        </Box>
      </Box>

      <Dialog open={open} onClose={finish} maxWidth="xs" fullWidth>
        <Box sx={{ padding: '18px 20px 12px' }}>
          <Typography variant="h4" sx={{ color: theme.palette.primary.main, marginBottom: '12px' }}>
            {label}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${plural}`}
            sx={{
              '& .MuiOutlinedInput-input': { padding: '9px 12px', fontSize: '14px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C9C9D1' }
            }}
          />
        </Box>

        <Box sx={{ maxHeight: '46vh', overflowY: 'auto', borderTop: '1px solid #EEEEF1', borderBottom: '1px solid #EEEEF1' }}>
          {shown.length === 0 && (
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#9A9EA3', padding: '16px 20px' }}>
              Nothing matches “{query}”.
            </Typography>
          )}
          {shown.slice(0, MAX_SHOWN).map((option) => (
            <Row
              key={option}
              option={option}
              checked={draft.includes(option)}
              onToggle={toggle}
              starred={isStarred(option)}
              onStar={star ? onStar : null}
            />
          ))}
          {shown.length > MAX_SHOWN && (
            <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', padding: '10px 16px' }}>
              {shown.length - MAX_SHOWN} more — narrow them with the search above.
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px 16px' }}>
          <Typography sx={{ flexGrow: 1, fontFamily: FONT, fontSize: '13px', color: '#9A9EA3' }}>
            {draft.length ? `${draft.length} chosen of ${options.length}` : `${options.length} available`}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => setDraft([])}
            sx={{
              border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
              padding: '7px 16px', fontFamily: FONT, fontSize: '13px',
              color: draft.length ? theme.palette.secondary.main : '#9A9EA3', cursor: 'pointer',
              '&:hover': { borderColor: theme.palette.secondary.main }
            }}
          >
            Clear
          </Box>
          <Box
            component="button"
            type="button"
            onClick={finish}
            sx={{
              border: 'none', backgroundColor: theme.palette.primary.light, borderRadius: '2px',
              padding: '8px 20px', fontFamily: FONT, fontSize: '13px', color: 'white', cursor: 'pointer',
              '&:hover': { backgroundColor: theme.palette.primary.main }
            }}
          >
            Done
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

export default PickerDialog
